import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import heroImage from "../assets/figma/hero.png";
import facilityImage from "../assets/figma/media-2.png";
import { Container, SiteFooter, SiteHeader } from "./SiteChrome";
import { useCmsBatch } from "../hooks/useCmsBatch";
import { CmsIcon } from "../utils/cmsIcons";

const valuesDefault = [
  { icon: "Gauge", title: "Objective testing", body: "Every player is measured against the same benchmarks, so progress is never a guess." },
  { icon: "Users", title: "Expert coaching", body: "A certified coaching staff focused on technical development at every age." },
  { icon: "TrendingUp", title: "Measurable progress", body: "Assessments repeat over time, so growth shows up in the numbers, not just the highlight reel." },
  { icon: "ShieldCheck", title: "Safe, structured training", body: "Purpose-built sessions in a controlled, weather-proof environment." },
];
const CMS_KEYS = ["about-hero", "about-approach", "about-facility", "coaching-staff"];

export function AboutPage() {
  const { content } = useCmsBatch(CMS_KEYS);
  const hero = content["about-hero"] || {};
  const approach = content["about-approach"] || {};
  const facility = content["about-facility"] || {};
  const staff = content["coaching-staff"] || {};
  const coaches = staff.items?.filter((coach) => coach.title) || [];
  const values = approach.items?.length ? approach.items : valuesDefault;

  return (
    <>
      <SiteHeader />
      <main>
        <section className="section light">
          <Container className="grid-12">
            <div className="section-copy">
              <p className="eyebrow"><span>01</span>About Us</p>
              <h2>{hero.title || "Built for the next generation of players."}</h2>
              <p className="lead">{hero.subtitle || "We take the guesswork out of player development."}</p>
              <p>
                {hero.body || "Formula Soccer Center combines objective performance testing with expert coaching, so every player — from their first touch to elite competition — has a clear, measurable path forward. Inside our indoor training lab, players train on premium turf, get assessed against a consistent set of benchmarks, and track their progress over time."}
              </p>
            </div>
            <div className="media-split-image">
              <img src={hero.mediaUrl || heroImage} alt="Player training inside Formula Soccer Center" />
            </div>
          </Container>
        </section>

        <section className="section dark">
          <Container>
            <p className="eyebrow lime"><span>02</span>Our approach</p>
            <h2 style={{ fontSize: "clamp(32px, 4vw, 52px)" }}>{approach.title || "What sets us apart."}</h2>
            <div className="value-grid">
              {values.map((value) => (
                <div key={value.title} className="value-card">
                  <div className="value-card-icon"><CmsIcon name={value.icon} size={20} strokeWidth={1.5} /></div>
                  <h3>{value.title}</h3>
                  <p>{value.body}</p>
                </div>
              ))}
            </div>
          </Container>
        </section>

        <section className="section light">
          <Container>
            <p className="eyebrow"><span>03</span>Coaching staff</p>
            <h2 style={{ fontSize: "clamp(32px, 4vw, 52px)" }}>Coached with purpose.</h2>
            <p style={{ color: "var(--muted)", lineHeight: 1.8, marginTop: 16, maxWidth: 680 }}>
              Our certified coaching team uses a purpose-driven training approach backed by the latest
              sports science — creating an immersive, data-rich experience that helps every player clearly
              track progress and take their game to the next level.
            </p>
            {coaches.length > 0 ? (
              <div className="coach-grid">
                {coaches.map((coach, index) => (
                  <div className="coach-card" key={`${coach.title}-${index}`}>
                    {coach.mediaUrl && <img className="coach-card-photo" src={coach.mediaUrl} alt={coach.title} />}
                    <h3>{coach.title}</h3>
                    {coach.value && <p className="coach-card-role">{coach.value}</p>}
                    {coach.body && <p className="coach-card-bio">{coach.body}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: "var(--muted)", lineHeight: 1.8, marginTop: 16 }}>
                Individual coach profiles are on their way — check back soon.
              </p>
            )}
            {staff.body && (
              <p style={{ color: "var(--muted)", lineHeight: 1.8, marginTop: 24, maxWidth: 680 }}>{staff.body}</p>
            )}
          </Container>
        </section>

        <section className="section dark">
          <Container className="grid-12">
            <div className="section-copy">
              <p className="eyebrow lime"><span>04</span>Facility</p>
              <h2 style={{ fontSize: "clamp(32px, 4vw, 52px)" }}>{facility.title || "Step inside the lab."}</h2>
              <p style={{ color: "var(--muted)", lineHeight: 1.8, marginTop: 16 }}>
                {facility.body || "From our turf arenas to the performance lab and recovery zone, see what makes Formula Soccer Center different."}
              </p>
              <Link className="button button--lime" to="/facility" style={{ marginTop: 24, display: "inline-flex", alignItems: "center", gap: 8 }}>
                Explore the facility <ArrowUpRight size={16} strokeWidth={1.5} />
              </Link>
            </div>
            <div className="media-split-image">
              <img src={facilityImage} alt="Inside the Formula Soccer Center facility" />
            </div>
          </Container>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}
